import { useRef, useState, useEffect, useCallback, type RefObject } from 'react'

interface Dimensions {
  width: number
  height: number
}

export function useChartDimensions(
  defaultWidth = 600,
  defaultHeight = 400
): [RefObject<HTMLDivElement | null>, Dimensions] {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: defaultWidth,
    height: defaultHeight,
  })

  const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
    for (const entry of entries) {
      const { width, height } = entry.contentRect
      if (width > 0 && height > 0) {
        setDimensions({ width: Math.floor(width), height: Math.floor(height) })
      }
    }
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new ResizeObserver(handleResize)
    observer.observe(el)

    // Set initial dimensions
    const rect = el.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) {
      setDimensions({ width: Math.floor(rect.width), height: Math.floor(rect.height) })
    }

    return () => observer.disconnect()
  }, [handleResize])

  return [containerRef, dimensions]
}
